/**
 * useOnboardingStatus Hook (V86)
 *
 * 사용자의 온보딩 상태 (약관 동의/비밀번호 변경)를 확인합니다.
 *
 * **V86 변경:**
 * - Firestore 직접 접근 → httpsCallable 방식으로 전환
 * - 실시간 구독 → 폴링 방식으로 변경
 * - 보안 규칙 위반 문제 해결
 */

import { useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../firebase/config';

export interface OnboardingStatus {
  isLoading: boolean;
  isAuthenticated: boolean;
  needsTermsAcceptance: boolean;
  needsPasswordChange: boolean;
  currentTermsVersion: string | null;
  userTermsVersion: string | null;
  error: string | null;
  // V37: 권한 체크용
  userType: 'admin' | 'employee' | null;
  roleIds: string[];
  hasNoAccess: boolean; // employee이면서 roleIds가 비어있는 경우
}

export function useOnboardingStatus(): OnboardingStatus {
  const [status, setStatus] = useState<OnboardingStatus>({
    isLoading: true,
    isAuthenticated: false,
    needsTermsAcceptance: false,
    needsPasswordChange: false,
    currentTermsVersion: null,
    userTermsVersion: null,
    error: null,
    userType: null,
    roleIds: [],
    hasNoAccess: false,
  });

  // V86: httpsCallable을 통한 온보딩 상태 조회
  const fetchOnboardingStatus = useCallback(async () => {
    if (!functions) {
      return;
    }

    try {
      const getOnboardingStatusFn = httpsCallable<
        Record<string, never>,
        {
          currentTermsVersion: string | null;
          userTermsVersion: string | null;
          needsTermsAcceptance: boolean;
          needsPasswordChange: boolean;
          userType: 'admin' | 'employee' | null;
          roleIds: string[];
          hasNoAccess: boolean;
        }
      >(functions!, 'getOnboardingStatus');

      const result = await getOnboardingStatusFn({});
      const data = result.data;

      setStatus((prev) => ({
        ...prev,
        isLoading: false,
        isAuthenticated: true,
        currentTermsVersion: data.currentTermsVersion,
        userTermsVersion: data.userTermsVersion,
        needsTermsAcceptance: data.needsTermsAcceptance,
        needsPasswordChange: data.needsPasswordChange,
        userType: data.userType,
        roleIds: data.roleIds,
        hasNoAccess: data.hasNoAccess,
        error: null,
      }));
    } catch (err: any) {
      console.error('[useOnboardingStatus] 조회 실패:', err);

      // V55.38: unauthenticated 에러만 로그아웃 처리, 그 외 에러는 인증 상태 유지
      // Firebase Auth는 성공했지만 Cloud Function만 실패한 경우 로그아웃되지 않도록 함
      const errorCode = err.code || '';
      const isUnauthenticated =
        errorCode === 'unauthenticated' ||
        errorCode === 'functions/unauthenticated';

      setStatus((prev) => ({
        ...prev,
        isLoading: false,
        // unauthenticated가 아니면 인증 상태 유지
        isAuthenticated: isUnauthenticated ? false : true,
        error: err.message || '온보딩 상태 조회 실패',
      }));
    }
  }, []);

  useEffect(() => {
    // Firebase가 초기화되지 않은 경우 (데모 모드)
    if (!auth || !functions) {
      setStatus({
        isLoading: false,
        isAuthenticated: true,
        needsTermsAcceptance: false,
        needsPasswordChange: false,
        currentTermsVersion: null,
        userTermsVersion: null,
        error: null,
        userType: 'admin',
        roleIds: ['demo'],
        hasNoAccess: false,
      });
      return () => {};
    }

    const unsubscribeAuth = onAuthStateChanged(auth, async (user: FirebaseUser | null) => {
      if (!user) {
        setStatus({
          isLoading: false,
          isAuthenticated: false,
          needsTermsAcceptance: false,
          needsPasswordChange: false,
          currentTermsVersion: null,
          userTermsVersion: null,
          error: null,
          userType: null,
          roleIds: [],
          hasNoAccess: false,
        });
        return;
      }

      // V86: httpsCallable을 통해 온보딩 상태 조회
      setStatus((prev) => ({ ...prev, isLoading: true }));
      await fetchOnboardingStatus();
    });

    return () => {
      unsubscribeAuth();
    };
  }, [fetchOnboardingStatus]);

  return status;
}
